import { useEffect, useRef, useState } from "react";

interface UseLazyLoadImageOptions {
  /** The image source URL */
  src: string;
  /** Optional srcSet for responsive images */
  srcSet?: string;
  /** Margin around viewport to trigger loading (default: "0px 0px 600px 0px") */
  rootMargin?: string;
  /** Selector for scroll container (default: "#masonaryContainer") */
  containerSelector?: string;
}

type LoadedImageCacheEntry = {
  srcSet?: string;
};

// Cache loaded images so remounted cards can reuse loaded URLs immediately.
const loadedImageCache = new Map<string, LoadedImageCacheEntry>();
const MAX_LOADED_IMAGE_CACHE_ENTRIES = 600;

function getCachedImageState(src: string, requestedSrcSet?: string) {
  if (!src) {
    return { loadedSrc: undefined, loadedSrcSet: undefined };
  }

  const cached = loadedImageCache.get(src);
  if (!cached) {
    return { loadedSrc: undefined, loadedSrcSet: undefined };
  }

  return {
    loadedSrc: src,
    loadedSrcSet: requestedSrcSet ?? cached.srcSet,
  };
}

function cacheLoadedImage(src: string, srcSet?: string) {
  if (!src) return;
  // Maintain insertion order for simple LRU eviction.
  if (loadedImageCache.has(src)) {
    loadedImageCache.delete(src);
  }
  loadedImageCache.set(src, { srcSet });

  if (loadedImageCache.size > MAX_LOADED_IMAGE_CACHE_ENTRIES) {
    const oldestKey = loadedImageCache.keys().next().value;
    if (oldestKey) {
      loadedImageCache.delete(oldestKey);
    }
  }
}

/**
 * Custom hook for lazy loading images using Intersection Observer.
 *
 * Images start loading before they're visible based on the rootMargin.
 * Special handling for modals - images in #cartModal or #itemModal
 * load immediately without IntersectionObserver.
 *
 * @param options - Configuration options for lazy loading
 * @returns Object containing:
 * - imgRef: Ref to attach to the img element
 * - loadedSrc: The current loaded src (undefined until in viewport)
 * - loadedSrcSet: The current loaded srcSet (undefined until in viewport)
 *
 * @example
 * ```tsx
 * function LazyImage({ src, alt }) {
 *   const { imgRef, loadedSrc } = useLazyLoadImage({
 *     src,
 *     rootMargin: "0px 0px 400px 0px", // Load 400px before entering viewport
 *   });
 *
 *   return (
 *     <img
 *       ref={imgRef}
 *       src={loadedSrc || "/placeholder.jpg"}
 *       alt={alt}
 *       loading="lazy"
 *     />
 *   );
 * }
 * ```
 */
export const useLazyLoadImage = ({
  src,
  srcSet,
  rootMargin = "0px 0px 600px 0px",
  containerSelector = "#masonaryContainer",
}: UseLazyLoadImageOptions) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const initialState = getCachedImageState(src, srcSet);
  const [loadedSrc, setLoadedSrc] = useState<string | undefined>(initialState.loadedSrc);
  const [loadedSrcSet, setLoadedSrcSet] = useState<string | undefined>(initialState.loadedSrcSet);

  useEffect(() => {
    const imgElement = imgRef.current;
    if (!imgElement) return;

    const cachedState = getCachedImageState(src, srcSet);
    if (cachedState.loadedSrc) {
      setLoadedSrc(cachedState.loadedSrc);
      setLoadedSrcSet(cachedState.loadedSrcSet);
      return;
    }

    // Ensure a new image starts unloaded until observed.
    setLoadedSrc(undefined);
    setLoadedSrcSet(undefined);

    // Check if image is inside special containers that need eager loading
    const isInFavoritesModal = imgElement.closest("#cartModal");
    const isInItemModal = imgElement.closest("#itemModal");

    // If in special containers, load immediately without IntersectionObserver
    if (isInFavoritesModal || isInItemModal) {
      setLoadedSrc(src);
      if (srcSet) {
        setLoadedSrcSet(srcSet);
      }
      cacheLoadedImage(src, srcSet);
      return;
    }

    let scrollContainer: Element | null = null;
    scrollContainer = document.querySelector(containerSelector);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLoadedSrc(src);
            if (srcSet) {
              setLoadedSrcSet(srcSet);
            }
            cacheLoadedImage(src, srcSet);
            observer.unobserve(imgElement);
          }
        });
      },
      {
        root: scrollContainer,
        rootMargin,
        threshold: 0,
      }
    );

    // A virtualized grid can mount before its scroll container exists. Start
    // cards already near the viewport immediately so the first screen does
    // not sit on transparent placeholders while the observer catches up.
    if (!scrollContainer) {
      const { top } = imgElement.getBoundingClientRect();
      if (top <= window.innerHeight + 600) {
        setLoadedSrc(src);
        if (srcSet) {
          setLoadedSrcSet(srcSet);
        }
        cacheLoadedImage(src, srcSet);
        observer.unobserve(imgElement);
        return;
      }
    }

    observer.observe(imgElement);

    return () => {
      if (imgElement) {
        observer.unobserve(imgElement);
      }
    };
  }, [src, srcSet, rootMargin, containerSelector]);

  return { imgRef, loadedSrc, loadedSrcSet };
};
